// Admin Papierkorb store — signal-based, follows the adminAudit.ts pattern.
// GET /api/admin/trash — no pagination, the trash is expected to stay small
// (30-day auto-purge keeps it bounded).

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api } from "../lib/api";

export type TrashEntityType = "space" | "project" | "task" | "taskList" | "personalTask";

export interface TrashItem {
  entity_type: TrashEntityType;
  id: number;
  name: string;
  deleted_at: string;
  deleted_by_user_id: number | null;
  deleted_by_name: string | null;
  days_until_purge: number;
  context: string | null;
}

interface TrashResponse {
  items: TrashItem[];
  retention_days: number;
}

interface State {
  loading: boolean;
  items: TrashItem[];
  retentionDays: number;
  error: string | null;
}

const initial: State = {
  loading: true,
  items: [],
  retentionDays: 30,
  error: null,
};
const adminTrashSignal: Signal<State> = signal(initial);
let inFlight: Promise<void> | null = null;

async function load(): Promise<void> {
  adminTrashSignal.value = { ...adminTrashSignal.value, loading: true };
  try {
    const data = await api<TrashResponse>("/api/admin/trash");
    adminTrashSignal.value = {
      loading: false,
      items: data.items,
      retentionDays: data.retention_days,
      error: null,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Unknown error";
    adminTrashSignal.value = { ...adminTrashSignal.value, loading: false, error: msg };
  } finally {
    inFlight = null;
  }
}

export function useAdminTrash(): State {
  useEffect(() => {
    if (!inFlight && adminTrashSignal.value.loading) {
      inFlight = load();
    }
  }, []);
  return adminTrashSignal.value;
}

export function reloadAdminTrash(): void {
  inFlight = load();
}

export async function restoreTrashItem(
  entityType: TrashEntityType,
  id: number
): Promise<void> {
  await api(`/api/admin/trash/${entityType}/${id}/restore`, { method: "POST" });
  reloadAdminTrash();
}

export async function purgeTrashItem(
  entityType: TrashEntityType,
  id: number
): Promise<void> {
  await api(`/api/admin/trash/${entityType}/${id}`, { method: "DELETE" });
  reloadAdminTrash();
}
